import type { BlogPost } from '../types'

export const FOTOVOLTAICA_Y_BOMBA_DE_CALOR_COMBINADAS: BlogPost = {
  slug: 'fotovoltaica-y-bomba-de-calor-combinadas',
  lang: 'es',
  topicId: 'photovoltaik-waermepumpe-kombinieren',
  title: 'Combinar fotovoltaica y bomba de calor: lo que realmente aporta',
  metaDescription:
    'Por qué fotovoltaica y bomba de calor se complementan, qué significa esto para su factura de ' +
    'la luz y en qué fijarse al dimensionar. Actualizado: julio de 2026.',
  keywords: [
    'combinar fotovoltaica y bomba de calor',
    'bomba de calor con electricidad solar',
    'autoconsumo fotovoltaica bomba de calor',
    'comparar presupuestos bomba de calor',
    'coste instalación solar y bomba de calor',
  ],
  publishedAt: '2026-07-21',
  excerpt:
    'La instalación solar y la bomba de calor suelen plantearse por separado - pero se refuerzan ' +
    'mutuamente: la bomba de calor se convierte en el mayor consumidor eléctrico de la vivienda, la ' +
    'instalación fotovoltaica le aporta precisamente esa electricidad propia y económica. Qué implica ' +
    'esto para la planificación, el dimensionado y el coste.',
  sections: [
    {
      type: 'paragraph',
      text:
        'Fotovoltaica y bomba de calor suelen tratarse por separado al solicitar presupuesto - primero ' +
        'la instalación solar, después (o de forma independiente) la nueva calefacción. Técnicamente, ' +
        'sin embargo, ambos temas están más relacionados de lo que sugiere esa planificación separada: ' +
        'una bomba de calor es, con diferencia, el mayor consumidor eléctrico de una vivienda ' +
        'unifamiliar, muy por delante del frigorífico, la lavadora o la iluminación juntos. Una ' +
        'instalación fotovoltaica aporta precisamente el recurso que necesita una bomba de calor: ' +
        'electricidad. Quien planifica ambos elementos de forma conjunta, en lugar de considerarlos ' +
        'de forma aislada, acaba tomando mejores decisiones económicas.',
    },
    {
      type: 'heading',
      text: 'Por qué fotovoltaica y bomba de calor se complementan',
    },
    {
      type: 'paragraph',
      text:
        'Una bomba de calor convierte electricidad en un múltiplo de esa cantidad en calor (eficiencia, ' +
        'medida como coeficiente de rendimiento anual). Que esa energía provenga de la red al precio ' +
        'actual de la electricidad o de su propia instalación solar no cambia la potencia calorífica, ' +
        'pero sí notablemente el coste por kilovatio-hora de calor. Al mismo tiempo, la bomba de calor ' +
        'es un consumidor grande y relativamente flexible: la producción de agua caliente y - con un ' +
        'depósito de inercia suficientemente dimensionado - también parte de la calefacción pueden ' +
        'desplazarse a las horas de más sol del día, en lugar de funcionar de forma rígida según la ' +
        'demanda. Precisamente en esa flexibilidad reside el verdadero valor añadido de la combinación: ' +
        'la instalación fotovoltaica obtiene un consumidor grande y bien controlable para su ' +
        'electricidad, en lugar de verter el excedente del mediodía a la red por poco dinero.',
    },
    {
      type: 'heading',
      text: 'El efecto en su factura eléctrica',
    },
    {
      type: 'paragraph',
      text:
        'Sin bomba de calor, una instalación fotovoltaica típica de vivienda unifamiliar suele cubrir ' +
        'directamente solo una parte del consumo eléctrico anual - una gran parte de la generación se ' +
        'vierte a la red durante el día, mientras que por la tarde y la noche se vuelve a comprar ' +
        'electricidad de red. Una bomba de calor aumenta notablemente el consumo total, pero también ' +
        'desplaza el perfil de consumo: si se hace funcionar (automáticamente mediante un control ' +
        'compatible o manualmente) preferentemente cuando la instalación fotovoltaica genera mucha ' +
        'electricidad, la proporción de electricidad autoconsumida sobre el consumo total (tasa de ' +
        'autoconsumo) aumenta claramente. Cada kWh que se usa así directamente, en lugar de venderse a ' +
        'una tarifa de excedente baja y recomprarse después más cara, mejora la rentabilidad de ambas ' +
        'instalaciones conjuntamente.',
    },
    {
      type: 'heading',
      text: 'En qué fijarse al combinar ambos sistemas',
    },
    {
      type: 'list',
      items: [
        'Dimensione la fotovoltaica según la necesidad real: el consumo eléctrico anual incluyendo la bomba de calor (no solo el consumo doméstico previo sin calefacción) debería entrar en el cálculo del tamaño de la instalación',
        'Revise el depósito de inercia/capacidad de almacenamiento térmico: un depósito suficientemente grande permite almacenar el excedente fotovoltaico "térmicamente", en lugar de hacer funcionar la bomba de calor solo exactamente según la demanda momentánea',
        'Compare tarifa eléctrica para bomba de calor frente a autoconsumo directo: una tarifa reducida para bomba de calor con contador separado puede compensar o no - depende de la relación entre la tasa de autoconsumo y la diferencia de tarifa, y es un cálculo caso por caso',
        'Tenga en cuenta la temperatura de impulsión y la distribución del calor: la calefacción por suelo radiante permite temperaturas de impulsión más bajas y por tanto una bomba de calor más eficiente que los radiadores clásicos - esto influye en cuánto compensa realmente la combinación con fotovoltaica',
        'Revise por separado los programas de ayudas: las ayudas para fotovoltaica y para bomba de calor van por programas distintos con requisitos propios - algunos municipios subvencionan adicionalmente la combinación, pero no está regulado de forma uniforme',
      ],
    },
    {
      type: 'heading',
      text: '¿Primero la bomba de calor, primero la fotovoltaica, o planificar juntas?',
    },
    {
      type: 'paragraph',
      text:
        'No existe un orden universalmente correcto - depende del estado de reforma del edificio, la ' +
        'calefacción existente y el presupuesto disponible. Donde sea posible, sí compensa una ' +
        'planificación conjunta: el tamaño adecuado de la instalación fotovoltaica depende en gran ' +
        'medida de si y cómo una bomba de calor cambiará el consumo eléctrico. Si primero se dimensiona ' +
        'la instalación fotovoltaica sin tener en cuenta una futura bomba de calor, después suele ' +
        'quedarse pequeña para cubrir una parte relevante del nuevo consumo.',
    },
    {
      type: 'heading',
      text: 'Cómo ayuda Roofora',
    },
    {
      type: 'paragraph',
      text:
        'En Roofora puede solicitar fotovoltaica (sistema completo, solo montaje en tejado o solo ' +
        'instalación eléctrica) y bomba de calor como solicitudes propias y anónimas, y comparar ' +
        'presupuestos con precio fijo de empresas verificadas de forma independiente para cada una - ' +
        'sin lluvia de llamadas, con el contacto liberado solo tras su autorización activa para cada ' +
        'empresa. Así puede licitar ambos proyectos por separado, pero planificándolos con vistas a la ' +
        'combinación, en lugar de comprometerse precipitadamente con un único proveedor para ambos.',
    },
    {
      type: 'heading',
      text: 'Aviso importante',
    },
    {
      type: 'paragraph',
      text:
        'Todos los datos de este artículo corresponden a julio de 2026 y son de carácter general - ' +
        'las tasas de ayuda, las tarifas eléctricas y la rentabilidad real de una combinación dependen ' +
        'en gran medida del caso concreto (edificio, consumo, región). Este artículo no sustituye un ' +
        'asesoramiento energético o fiscal individual.',
    },
  ],
  relatedLinks: [
    { label: '¿Cuánto cuesta una instalación solar?', href: '/coste-instalacion-fotovoltaica' },
    { label: 'Planificar la instalación paso a paso', href: '/planificar-instalacion-fotovoltaica' },
    { label: 'Fotovoltaica y bomba de calor: comparar presupuestos', href: '/fotovoltaica-bomba-de-calor' },
  ],
}
